<template lang="pug">
    .customer-transfer(v-loading="loading")
        //- (v-if="this.actions.initialize === null")
        .alert.info(v-if="info")
            p {{ info }}
        .alert.danger(v-if="error")
            p {{ error }}
            a(href="#" :disabled="disabled" @click.prevent.stop="initialize") {{ $t('Try again') }}
        .form(v-else)
            //- must NOT be gated on finished_at: cancel/fail/finish all set it, which used to
            //- hide this whole panel and with it the .canceled/.failed/.finished branches below,
            //- so a cancelled transfer just vanished with no confirmation. Matches ProductImport.
            .transfer-info-panel(v-if="currentTransfer")
                //- When the microservice manages a StatusLog we render the shared StatusLog
                //- component the rest of the admin uses, fed by the status-log.{id} channel.
                //- The per-status blocks below are the legacy fallback, kept because several
                //- microservices manage no status log at all - never both at once.
                .status-logs-container.z-depth-1.live-status(v-if="useLiveStatusLog")
                    StatusLog(:log="statusLog")
                        template(slot="actions")
                            .estimate(v-if="currentTransfer.status === 'running' && estimatedCompletion")
                                | {{ $t('Estimated completion: {time}.', { time: estimatedCompletion }) }}
                            .import-controls
                                el-button.pause-import(
                                    v-if="currentTransfer.status === 'running' && controlUrl('pause')"
                                    size="mini"
                                    icon="el-icon-fa-pause fas"
                                    :loading="controlPending === 'pause'"
                                    @click.stop.prevent="pause"
                                )
                                    | {{ $t('Pause') }}
                                el-button.resume-import(
                                    v-if="currentTransfer.status === 'paused' && controlUrl('resume')"
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-fa-play fas"
                                    :loading="controlPending === 'resume'"
                                    @click.stop.prevent="resume"
                                )
                                    | {{ $t('Continue') }}
                                el-button.stop-import(
                                    v-if="controlUrl('stop')"
                                    size="mini"
                                    type="danger"
                                    icon="el-icon-fa-ban fas"
                                    :loading="controlPending === 'stop'"
                                    @click.stop.prevent="stop"
                                )
                                    | {{ $t('Cancel') }}
                template(v-else-if="showLegacyProgress")
                    .fresh(v-if="currentTransfer.status === 'fresh'")
                        .alert.primary
                            p {{ $t(`Your ${type} has been queued and will start as soon as possible.`) }}
                            p {{ $t('Please check later') }},&nbsp
                                a(href="#" :disabled="disabled" @click.stop.prevent="refresh") {{ $t('or refresh now') }}
                    .pending(v-if="currentTransfer.status === 'pending'")
                        .alert.primary
                            p {{ $t(`An ${type} is in the queue and will start as soon as possible.`) }}
                            p {{ $t('Please check later') }},&nbsp
                                a(href="#" :disabled="disabled" @click.stop.prevent="refresh") {{ $t('or refresh now') }}
                            //- a queued transfer must be cancellable too, otherwise resuming
                            //- leaves the operator with no way out until the job picks it up
                            .import-controls
                                el-button.stop-import(
                                    v-if="controlUrl('stop')"
                                    size="mini"
                                    type="danger"
                                    icon="el-icon-fa-ban fas"
                                    :loading="controlPending === 'stop'"
                                    @click.stop.prevent="stop"
                                )
                                    | {{ $t('Cancel') }}
                    .running(v-if="currentTransfer.status === 'running'")
                        // .percentage
                        //     el-progress(:stroke-width="6" :percentage="Math.round(currentTransfer.percentage * 10) / 10")
                        .alert.primary
                            .percentage
                                el-progress(
                                    type="dashboard"
                                    :width="100"
                                    :percentage="currentTransfer.percentage"
                                    :format="percentage => `${Math.round(percentage * 100) / 100}%`"
                                )
                            .info(v-if="currentTransfer.percentage < 100")
                                b {{ $t(`An ${type} is currently running.`) }}
                                //- p {{ $t(`The ${type} has been started {time} ago and is currently at {percentage}%.`, { time: formatDistanceToNow(currentTransfer.started_at), percentage: Math.round(currentTransfer.percentage * 100) / 100 }) }}
                                p {{ $t('Started: {time}', { time: formatDistanceToNow(currentTransfer.started_at), percentage: Math.round(currentTransfer.percentage * 100) / 100 }) }}
                                p(v-if="estimatedCompletion") {{ $t('Estimated completion: {time}.', { time: estimatedCompletion }) }}
                                //- exactly one progress source is ever active: with a status log
                                //- the bar is driven by the websocket and needs no manual refresh,
                                //- otherwise it is polled and the operator gets the refresh link
                                p(v-if="!liveProgress") {{ $t('Please check later') }},&nbsp
                                    a(href="#" :disabled="disabled" @click.stop.prevent="refresh") {{ $t('or refresh now') }}
                                .import-controls
                                    el-button.pause-import(
                                        v-if="controlUrl('pause')"
                                        size="mini"
                                        icon="el-icon-fa-pause fas"
                                        :loading="controlPending === 'pause'"
                                        @click.stop.prevent="pause"
                                    )
                                        | {{ $t('Pause') }}
                                    el-button.stop-import(
                                        v-if="controlUrl('stop')"
                                        size="mini"
                                        type="danger"
                                        icon="el-icon-fa-ban fas"
                                        :loading="controlPending === 'stop'"
                                        @click.stop.prevent="stop"
                                    )
                                        | {{ $t('Cancel') }}
                            .info(v-else)
                                p {{ $t(`The ${type} has been completed.`) }}
                    .paused(v-if="currentTransfer.status === 'paused'")
                        .alert.primary
                            .percentage
                                //- muted ring: a held transfer must not read as a live one
                                el-progress(
                                    type="dashboard"
                                    color="#909399"
                                    :width="100"
                                    :percentage="currentTransfer.percentage"
                                    :format="percentage => `${Math.round(percentage * 100) / 100}%`"
                                )
                            .info
                                p {{ $t('Paused. It will continue where it stopped.') }}
                                .import-controls
                                    el-button.resume-import(
                                        v-if="controlUrl('resume')"
                                        size="mini"
                                        type="primary"
                                        icon="el-icon-fa-play fas"
                                        :loading="controlPending === 'resume'"
                                        @click.stop.prevent="resume"
                                    )
                                        | {{ $t('Continue') }}
                                    el-button.stop-import(
                                        v-if="controlUrl('stop')"
                                        size="mini"
                                        type="danger"
                                        icon="el-icon-fa-ban fas"
                                        :loading="controlPending === 'stop'"
                                        @click.stop.prevent="stop"
                                    )
                                        | {{ $t('Cancel') }}
                .canceled(v-if="currentTransfer.status === 'canceled'")
                    .alert.warning
                        p {{ $t('Cancelled {time} ago. Everything transferred before that was kept.', { time: formatDistanceToNow(currentTransfer.finished_at) }) }}
                .failed(v-if="currentTransfer.status === 'failed'")
                    .alert.danger
                        p {{ $t(`The last ${type} could not be completed successfull. It failed {time} ago.`, { time: formatDistanceToNow(currentTransfer.finished_at) }) }}
                .finished(v-if="currentTransfer.status === 'finished'")
                    .alert.success
                        p {{ $t(`The Last ${type} has been finished successfully {time} ago.`, { time: formatDistanceToNow(currentTransfer.finished_at) }) }}

            //- one source of truth with the progress panel above: while a transfer is active the
            //- form must be gone, or an operator can start a second one on top of a running job
            el-form.dynamic-form(v-if="!transferIsActive")
                template(v-for="field in additionalFieldConfigs")
                    InputField(:config="field" :form="form" :key="field.key")
                .alert.info
                    .transfer-content
                        h4.heading {{ config.name }}
                        //- TODO: informationen zu den
                        //- zu importierenden Kunden anfragen (initialize)
                        //- und anzeigen
                        //- --> Gesamtzahl der Kunden
                        //- --> Anzahl der bereits importierten Kunden
                        //- --> Anzahl der Neukunden
                        p(v-if="totalNumberOfCustomers != null && totalNumberOfCustomers > 0")
                            | {{ $t(`${type} {number} people`, { number: totalNumberOfCustomers }) }}
                        p(v-else)
                            | {{ $t(`There is no one to ${type}`) }}
                    template(v-if="ignoreExistingCustomersConfig")
                        InputField.no-margin(:config="ignoreExistingCustomersConfig" :form="form" :key="ignoreExistingCustomersConfig.key")
                    el-form-item.buttons.no-margin(v-if="totalNumberOfCustomers != null && totalNumberOfCustomers > 0")
                        el-button.start-transfer(
                            type="primary"
                            :disabled="!totalNumberOfCustomers"
                            @click="start"
                        )
                            | {{ $t(`${type}`) }}
</template>

<i18n>
{
    "en": {
        "Technical issue! {name} could not be initialized.": "Technical issue! {name} could not be initialized.",
        "Cancelled {time} ago. Everything transferred before that was kept.": "Cancelled {time} ago. Everything transferred before that was kept.",
        "This could not be updated ({action}).": "This could not be updated ({action}).",
        "{name} could not be started.": "{name} could not be started.",
        "import {number} people": "Import {number} people",
        "import": "Import",
        "export {number} people": "Export {number} people",
        "export": "Export",
        "Started: {time}": "Started: {time}",
        "Estimated completion: {time}.": "Estimated completion: {time}.",
        "The import has been started {time} ago and is currently at {percentage}%.": "The import has been started {time} ago and is currently at {percentage}%.",
        "The Last import has been finished successfully {time} ago.": "The Last import has been finished successfully {time} ago.",
        "The last import could not be completed successfull. It failed {time} ago.": "The last import could not be completed successfull. It failed {time} ago.",
        "The export has been started {time} ago and is currently at {percentage}%.": "The export has been started {time} ago and is currently at {percentage}%.",
        "The Last export has been finished successfully {time} ago.": "The Last export has been finished successfully {time} ago.",
        "The last export could not be completed successfull. It failed {time} ago.": "The last export could not be completed successfull. It failed {time} ago."
    },
    "de": {
        "Technical issue! {name} could not be initialized.": "Technisches Problem! {name} konnte nicht initialisiert werden.",
        "Pause": "Pausieren",
        "Continue": "Fortsetzen",
        "Cancel": "Abbrechen",
        "Paused. It will continue where it stopped.": "Pausiert. Es wird an der unterbrochenen Stelle fortgesetzt.",
        "Cancelled {time} ago. Everything transferred before that was kept.": "Vor {time} abgebrochen. Alles zuvor \u00dcbertragene wurde beibehalten.",
        "This could not be updated ({action}).": "Dies konnte nicht aktualisiert werden ({action}).",
        "{name} could not be started.": "{name} konnte nicht gestartet werden.",
        "An import is in the queue and will start as soon as possible.": "Aktuell befindet sich bereits ein Import in der Warteschlange und wird so schnell wie möglich gestartet.",
        "An import is currently running.": "Aktuell befindet sich bereits ein Import in der Verarbeitung.",
        "import {number} people": "Importiere {number} Personen",
        "There is no one to import": "Es gibt niemanden zu importieren",
        "The import has been started {time} ago and is currently at {percentage}%.": "Der Import wurde vor {time} gestartet und ist aktuell bei {percentage}%.",
        "Started: {time}": "Gestartet vor: {time}",
        "The Last import has been finished successfully {time} ago.": "Der letzte Import wurde erfolgreich abgeschlossen vor {time}.",
        "The last import could not be completed successfull. It failed {time} ago.": "Der letzte Import konnte nicht erfolgreich abgeschlossen werden. Fehlgeschlagen vor {time}.",
        "Estimated completion: {time}.": "Voraussichtliche Dauer bis zur Fertigstellung: {time}",
        "Your import has been queued and will start as soon as possible.": "Dein Import wurde erfolgreich erfasst und wird so schnell wie möglich gestartet.",
        "Please check later": "Schau doch später nochmal vorbei",
        "The import has been completed.": "Der Import wurde erfolgreich abgeschlossen.",
        "or refresh now": "oder aktualisiere jetzt",
        "import": "Importieren",
        "Try again": "Erneut versuchen",
        "An export is in the queue and will start as soon as possible.": "Aktuell befindet sich bereits ein Export in der Warteschlange und wird so schnell wie möglich gestartet.",
        "An export is currently running.": "Aktuell befindet sich bereits ein Export in der Verarbeitung.",
        "export {number} people": "Exportiere {number} Personen",
        "There is no one to export": "Es gibt niemanden zu exportieren",
        "The export has been started {time} ago and is currently at {percentage}%.": "Der Export wurde vor {time} gestartet und ist aktuell bei {percentage}%.",
        "The Last export has been finished successfully {time} ago.": "Der letzte Export wurde erfolgreich abgeschlossen vor {time}.",
        "The last export could not be completed successfull. It failed {time} ago.": "Der letzte Export konnte nicht erfolgreich abgeschlossen werden. Fehlgeschlagen vor {time}.",
        "Your export has been queued and will start as soon as possible.": "Dein Export wurde erfolgreich erfasst und wird so schnell wie möglich gestartet.",
        "The export has been completed.": "Der Export wurde erfolgreich abgeschlossen.",
        "export": "Exportieren"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { formatDistanceToNow, fromUnixTime, getUnixTime } from 'date-fns';
import { activeDateLocale } from '@/utils/dateLocale';

import { adminClient as apolloClient } from '@/store/apollo';

import CustomerTransferApi from '@/extensions/shop/store/api/customerTransfer';
import StatusLogApi from '@/store/api/system/statusLog';
import StatusLog from '@/views/cms/Dashboard/widgets/logs/Partials/StatusLog';
import InputField from './CustomerTransferInputField';
import { useSetupStore } from '@/stores';
import Echo from '@/echo';

export default {
    name: 'CustomerTransfer',
    props: {
        type: {
            type: String,
            default: 'import'
        },
        config: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            info: false,
            error: false,
            loading: false,
            actions: {
                initialize: undefined,
                start: undefined,
                stop: undefined,
                pause: undefined,
                resume: undefined,
                refresh: undefined,
            },
            // which control is mid-request, so only that button spins
            controlPending: false,
            // exactly one of these is ever active - see applyProgressMode()
            statusLogChannel: undefined,
            pollTimer: undefined,
            // the StatusLog row the shared component renders, seeded once then kept
            // current by the status-log.{id} broadcasts
            statusLog: null,
            // true while the seed request is in flight, and whether it failed - together
            // these decide whether the legacy block is allowed to render at all
            statusLogFetching: false,
            statusLogFailed: false,
            currentTransfer: undefined,
            totalNumberOfCustomers: undefined,
            form: {}
        }
    },
    components: {
        InputField,
        StatusLog,
    },
    computed: {
        // The websocket bar REPLACES the polled one, and only when the microservice actually
        // delivers a status log. Several services keep none at all, so polling is a
        // permanently supported mode, not a temporary fallback. Decided from the data, never
        // from a service list: a service that has the column can still return null.
        liveProgress() {
            return Boolean(_.get(this.currentTransfer, 'status_log_id')) && Boolean(Echo.client);
        },
        transferIsActive() {
            return ['fresh', 'pending', 'running', 'paused'].includes(_.get(this.currentTransfer, 'status'));
        },
        // a queued row has not produced a status log event yet, so there is nothing to listen
        // to until the job actually starts - see applyProgressMode()
        transferIsQueued() {
            return ['fresh', 'pending'].includes(_.get(this.currentTransfer, 'status'));
        },
        // the shared StatusLog component takes over only once we actually hold a log row
        useLiveStatusLog() {
            return this.liveProgress && this.transferIsActive && Boolean(this.statusLog);
        },
        // The legacy blocks exist for microservices that keep no status log at all.
        // Once one exists we always show the shared component - including while the row
        // is queued after a resume, where the last known log still describes the run.
        // Only a failed seed falls back, so the panel can never end up blank.
        showLegacyProgress() {
            return !this.liveProgress || this.statusLogFailed;
        },
        estimatedCompletion() {
            if (!this.currentTransfer || this.currentTransfer.percentage <= 0) return false;

            const secondsSinceStarted = getUnixTime(new Date) - this.currentTransfer.started_at;
            const estimatedDuration = parseInt(secondsSinceStarted / this.currentTransfer.percentage * 100);

            return this.formatDistanceToNow(estimatedDuration + parseInt(this.currentTransfer.started_at));
        },
        ignoreExistingCustomersConfig() {
            const fields = _.get(this.config, 'fields') || []
            const config = fields.find(el => el.key === 'ignore-existing-customers')
            return config
        },
        additionalFieldConfigs() {
            const fields = _.get(this.config, 'fields') || []
            return fields.filter(el => ['ignore-existing-customers'].includes(el.key) === false)
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        settingValues() {
            return useSetupStore().settingValues;
        }
    },
    methods: {
        formatDistanceToNow(timestamp) {
            return formatDistanceToNow(fromUnixTime(parseInt(timestamp)), { locale: activeDateLocale() })
        },
        async refresh() {
            await apolloClient.clearStore()
            this.initialize()
            this.$emit('refresh')
        },

        // ---- progress: websocket when the service keeps a status log, polling otherwise ----

        // Re-evaluated whenever currentTransfer is replaced. Tears the other mode down first
        // so the two can never run at once - a leaked interval behind a live bar looks
        // exactly like the feature working.
        applyProgressMode() {
            this.teardownProgress();

            if (!this.transferIsActive) {
                this.statusLog = null;
                return;
            }

            // Feed the shared component in EVERY active state, queued included. The log row
            // still describes this transfer across pause -> pending -> running, so dropping it
            // between modes is what made the legacy block appear after Continue.
            if (this.liveProgress) this.seedStatusLog();

            // A queued row emits no status log events at all - the job has not run yet - so
            // the socket has nothing to say until it starts. Polling is the only way to learn
            // that start/resume actually got picked up; without it the panel sits on the
            // queued branch forever. Once running, the socket takes over and polling stops.
            if (this.transferIsQueued || !this.liveProgress) this.startPolling();
            else this.subscribeToStatusLog();
        },

        seedStatusLog() {
            const logId = _.get(this.currentTransfer, 'status_log_id');

            if (!logId) {
                this.statusLog = null;
                return;
            }

            // already holding this exact log - keep it rather than blanking the panel
            if (this.statusLog && String(this.statusLog.id) === String(logId)) return;

            this.fetchStatusLog(logId);
        },

        fetchStatusLog(logId) {
            this.statusLogFetching = true;
            this.statusLogFailed = false;

            return StatusLogApi.fetchList({ id: parseInt(logId) })
                .then(logs => {
                    this.statusLog = _.head(logs) || null;
                    this.statusLogFailed = !this.statusLog;
                })
                .catch(() => {
                    this.statusLog = null;
                    this.statusLogFailed = true;
                })
                .finally(() => { this.statusLogFetching = false; });
        },

        subscribeToStatusLog() {
            const logId = _.get(this.currentTransfer, 'status_log_id');
            if (!logId) return;

            if (!Echo.client) Echo.initialize();
            if (!Echo.client) return;

            this.statusLogChannel = `status-log.${logId}`;
            Echo.client.private(this.statusLogChannel)
                .listen('.status-log:updated', data => {
                    const log = _.get(data, 'log');
                    if (!log || !this.currentTransfer) return;

                    // feed the shared component the same way StatusLogsTable does
                    if (this.statusLog) {
                        ['status', 'progress', 'updated_at', 'heading', 'body', 'data'].forEach(key => {
                            this.$set(this.statusLog, key, log[key]);
                        });
                    } else {
                        this.seedStatusLog();
                    }

                    if (log.progress !== null && log.progress !== undefined) {
                        this.$set(this.currentTransfer, 'percentage', log.progress);
                    }

                    // a terminal log means the row moved on too, and the row carries what the
                    // log does not (finished_at, status)
                    if (['success', 'error', 'canceled'].includes(log.status)) this.initialize();
                });
        },

        startPolling() {
            // waiting for a queued job to be picked up is a short transition, so ask more
            // often than in the legacy steady state - initialize() re-runs applyProgressMode(),
            // which hands over to the socket the moment the row turns 'running'
            const interval = this.transferIsQueued ? 3000 : 10000;

            this.pollTimer = setInterval(() => {
                if (!this.transferIsActive) {
                    this.teardownProgress();
                    return;
                }
                this.initialize();
            }, interval);
        },

        teardownProgress() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = undefined;
            }
            if (this.statusLogChannel) {
                // per-log channel, exclusively ours, so leave rather than stopListening
                if (Echo.client) Echo.client.leave(this.statusLogChannel);
                this.statusLogChannel = undefined;
            }
        },

        // ---- controls ----

        stop() {
            return this.sendControl('stop');
        },
        pause() {
            return this.sendControl('pause');
        },
        resume() {
            return this.sendControl('resume');
        },

        // Resolve a `settings:` reference to its actual value. Returns undefined when the
        // shop has not configured that URL, which is how the buttons stay hidden rather than
        // rendering and then failing on click.
        resolveActionValue(value) {
            const prefix = 'settings:';
            if (!value) return undefined;
            if (!value.startsWith(prefix)) return value;

            return _.get(this.settingValues, value.substr(prefix.length, value.length - prefix.length)) || undefined;
        },

        controlUrl(action) {
            return this.resolveActionValue(_.get(this.actions, `${action}.url`));
        },

        sendControl(action) {
            const id = _.get(this.currentTransfer, 'id');
            if (!id || this.controlPending) return Promise.resolve();

            const url = this.controlUrl(action);
            const token = this.resolveActionValue(_.get(this.actions, `${action}.token`));

            if (!url) {
                this.notifyControlError(action);
                return Promise.resolve();
            }

            const requestConfig = token ? { headers: { Authorization: `Bearer ${token}` } } : null;

            this.controlPending = action;
            return CustomerTransferApi.REST(url, { id }, requestConfig)
                .then(({ data }) => {
                    this.currentTransfer = data;
                    this.applyProgressMode();
                })
                .catch(() => this.notifyControlError(action))
                .finally(() => { this.controlPending = false; });
        },

        notifyControlError(action) {
            this.$notify.error({
                title: this.$t('Error'),
                message: this.$t('This could not be updated ({action}).', { action }),
                iconClass: 'fas el-icon-fa-triangle-exclamation',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            });
        },
        initialize() {
            if (!this.actions.initialize) {
                this.error = this.$t('Technical issue! {name} could not be initialized.', { name: this.config.name })
                return;
            }

            const settings = this.settingValues;

            let api = this.actions.initialize.api;
            let url = this.actions.initialize.url;
            let token = this.actions.initialize.token;

            const prefix = 'settings:'
            if (url && url.startsWith('settings:')) {
                url = _.get(settings, url.substr(prefix.length, url.length - prefix.length))
            }
            if (token && token.startsWith('settings:')) {
                token = _.get(settings, token.substr(prefix.length, token.length - prefix.length))
            }

            // console.log(key, api, url, token, data);

            let method, config;
            switch (api.toLowerCase()) {
                case 'rest':
                    if (token) {
                        config = {
                            headers: {
                                Authorization: `Bearer ${token}`
                            }
                        };
                    }
                    method = CustomerTransferApi.REST;
                    break;

                // TODO
                // case 'graphql':
                //     method = MicroserviceApi.REST
                //     break;
            }

            // fetch customer transfer related stuff
            if (!method) {
                this.error = this.$t('Technical issue! {name} could not be initialized.', { name: this.config.name })
                return;
            }

            this.loading = true;

            const data = {
                cms: this.activeCms.id
            }
            return method(url, data, config)
                .then(({ data }) => {
                    this.error = false;
                    if (data[this.type]) {
                        this.currentTransfer = data[this.type];
                        // also the seed for the live bar: the socket only delivers the NEXT
                        // event, so without it a reload mid-run shows 0%
                        this.applyProgressMode();
                    }
                    this.totalNumberOfCustomers = _.get(data, 'total')
                })
                .catch(error => {
                    console.error(error);
                    this.error = this.$t('Technical issue! {name} could not be initialized.', { name: this.config.name })
                })
                .finally(() => {
                    this.loading = false;
                })
        },
        getTreeselectValueLabel(node) {
            let label = `${node.raw.label}`;
            const addPrefix = node => {
                if (node.parentNode) {
                    label = `${node.parentNode.raw.label} <i class="el-icon-fa-angle-right fas"></i> ${label}`
                    addPrefix(node.parentNode);
                }
            }
            addPrefix(node);
            return label;
        },
        start() {
            if (!this.actions.start) {
                this.$notify.error({
                    title: this.$t('An error occurred!'),
                    message: this.$t('{name} could not be started.', { name: this.config.name }),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
                return;
            }

            const settings = this.settingValues;

            let api = this.actions.start.api;
            let url = this.actions.start.url;
            let token = this.actions.start.token;

            const prefix = 'settings:'
            if (url && url.startsWith('settings:')) {
                url = _.get(settings, url.substr(prefix.length, url.length - prefix.length))
            }
            if (token && token.startsWith('settings:')) {
                token = _.get(settings, token.substr(prefix.length, token.length - prefix.length))
            }

            // console.log(key, api, url, token, data);

            let method, config;
            switch (api.toLowerCase()) {
                case 'rest':
                    if (token) {
                        config = {
                            headers: {
                                Authorization: `Bearer ${token}`
                            }
                        };
                    }
                    method = CustomerTransferApi.REST;
                    break;

                // TODO
                // case 'graphql':
                //     method = MicroserviceApi.REST
                //     break;
            }

            if (!method) {
                this.$notify.error({
                    title: this.$t('An error occurred!'),
                    message: this.$t('{name} could not be started.', { name: this.config.name }),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
                return;
            }

            this.loading = true;

            apolloClient.clearStore().then(() => {
                method(url, this.form, config)
                    .then(({ data }) => {
                        // reload form
                        this.currentTransfer = data
                        this.applyProgressMode();
                    })
                    .catch(error => {
                        /* eslint no-console: ["error", { allow: ["error"] }] */
                        console.error(error);
                        this.$notify.error({
                            title: this.$t('An error occurred!'),
                            message: this.$t('{name} could not be started.', { name: this.config.name }),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000,
                        })
                    })
                    .finally(() => {
                        this.loading = false;
                    })
            })
        },
    },
    mounted() {
        // const key = _.get(this.config, 'key');
        this.actions.initialize = _.get(this.config, 'initialize');
        this.actions.start = _.get(this.config, 'start');
        this.actions.stop = _.get(this.config, 'stop');
        this.actions.pause = _.get(this.config, 'pause');
        this.actions.resume = _.get(this.config, 'resume');
        this.actions.refresh = _.get(this.config, 'refresh');

        // set default field values
        const fields = _.get(this.config, 'fields') || []
        fields.forEach(field => {
            switch (field.type) {
                case 'radio':
                case 'boolean-button-group':
                    if (field.options && field.options.length) {
                        const defaultOption = field.options.find(el => el.default === true)
                        if (defaultOption) this.$set(this.form, field.key, ['true', '1', 'yes', true].includes(defaultOption.key))
                    } else {
                        this.$set(this.form, field.key, ['true', '1', 'yes', true].includes(field.key))
                    }
                    break;

                case 'switch':
                case 'checkbox':
                case 'boolean-button':
                    this.$set(this.form, field.key, ['true', '1', 'yes', true].includes(field.value))
                    break;

                default:
                    this.$set(this.form, field.key, field.value)

            }
        })

        this.form.cms = this.activeCms.id

        this.initialize()
    },
    beforeDestroy() {
        this.teardownProgress();
    }
}
</script>

<style lang="sass" scoped>
.customer-transfer
    min-height: 40px
    font-size: .95rem
    line-height: 1.35
    .import-controls
        display: flex
        gap: .5rem
        margin-top: .75rem

    //- inside the shared StatusLog the actions slot sits in a centred flex row,
    //- so the block-level spacing the legacy alerts need would misalign it
    .live-status
        .estimate
            white-space: nowrap
            color: $--color-text-secondary
        .import-controls
            margin-top: 0
    .alert
        margin: 0
        & + .alert
            margin-top: 1rem

    .transfer-content
        .heading
            margin-top: 0
    .option
        padding: 0.5rem 0
        small, b
            display: block
            line-height: initial

    .inline-form-fields
        display: flex
        .el-form-item + .el-form-item
            margin-left: 0.5rem

    .transfer-info-panel + .dynamic-form
        margin-top: 1rem

    .dynamic-form > .el-form-item
        margin: 1rem 0

    .spacer
        flex-grow: 1

    .margin-right
        margin-right: 1rem

    .el-button-group,
    .el-form-item.buttons
        white-space: nowrap

    .el-button-group
        .el-button.is-plain
            position: relative
            // border: 1px solid
            &:active,
            &:focus
                color: $--shop-color-primary
                background: lighten($--shop-color-primary, 35%)
                border-color: $--shop-color-primary
        .el-button.is-plain.el-button--primary
            z-index: 1
            // pointer-events: none
            &:hover
                color: $--shop-color-primary
                background: lighten($--shop-color-primary, 35%)
                border-color: $--shop-color-primary
    // .percentage
        margin-bottom: 0.5rem
        .el-progress.el-progress--line
            display: flex
            align-items: center
            justify-content: space-between
            ::v-deep .el-progress__text
                text-align: right
    //- every state that shows a progress ring shares one layout, so a new
    //- state cannot end up stacking the ring on top of its text again
    .running .alert,
    .paused .alert
        display: flex
        align-items: center
        .percentage
            flex: 0 0 auto
            margin-right: 1rem
            margin-bottom: -1rem
            ::v-deep
                .el-progress__text
                    padding-left: 0.5rem
                path.el-progress-circle__track
                    stroke: #dad4e5 !important
        .info
            flex: 1 1 auto
            min-width: 0

.transfer-content + .buttons
    margin-top: 0.5rem

a[disabled]
    opacity: 0.5
    pointer-events: none
</style>
